import React from "react";
import { useLocation } from "react-router-dom";
import { isEmpty } from "shared/utils";
import { screenStatusRegistry } from "./screenStatusRegistry";
import { ScreenContextConfig, ScreenLoadingConfig } from "./types";

const Context = React.createContext<ScreenContextConfig>({} as ScreenContextConfig);

/**
 * Context provider for managing loading states of screens.
 * It allows components to register their loading status and check if the screen is loaded.
 */
export function ScreenContextProvider({ children }: { children: React.ReactNode }) {
  const [screenLoadingConfig, setScreenLoadingConfig] = React.useState<ScreenLoadingConfig>({});

  const updateLoadingStatus = React.useCallback(
    (screenName: string, isFetched: boolean) => {
      setScreenLoadingConfig(prevConfig => ({
        ...prevConfig,
        [screenName]: isFetched
      }));
    },
    [setScreenLoadingConfig]
  );

  const getLoadingStatus = React.useCallback(
    (screenName: string) => {
      if (screenLoadingConfig.hasOwnProperty(screenName)) {
        return screenLoadingConfig[screenName];
      }
      return false;
    },
    [screenLoadingConfig]
  );

  const values = React.useMemo(() => {
    return {
      updateLoadingStatus,
      getLoadingStatus
    };
  }, [getLoadingStatus, updateLoadingStatus]);

  return <Context.Provider value={values}>{children}</Context.Provider>;
}

/**
 * Hook to register the current screen path and updates its loading status in the context.
 * @param isFetched - Indicates whether the api call is completed for the screen.
 */
export function useSetScreenLoadingStatus(isFetched: boolean) {
  const location = useLocation();
  screenStatusRegistry.register(location.pathname);
  const context = React.useContext(Context);
  if (isEmpty(context)) {
    throw new Error(`useSetScreenLoadingStatus must be used within a ScreenContextProvider`);
  }
  React.useEffect(() => {
    context.updateLoadingStatus(location.pathname, isFetched);

    return () => {
      context.updateLoadingStatus(location.pathname, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, location.pathname]);

  return null;
}

export function useIsScreenLoaded(): boolean {
  const location = useLocation();
  const isScreenRegistered = screenStatusRegistry.isScreenRegistered(location.pathname);
  const context = React.useContext(Context);

  if (!isScreenRegistered) {
    //if screen not registered, assume already loaded
    return true;
  }

  return context.getLoadingStatus(location.pathname);
}
